import { useCallback, useEffect, useState } from "react";

/**
 * Load reCAPTCHA script dynamically with site key
 */
const loadRecaptchaScript = (siteKey: string): Promise<void> => {
	return new Promise((resolve, reject) => {
		// Check if script is already loaded
		if (window.grecaptcha) {
			resolve();
			return;
		}

		// Check if script tag already exists
		const existingScript = document.querySelector('script[src*="recaptcha/api.js"]');
		if (existingScript) {
			// Wait for grecaptcha to be available
			const checkInterval = setInterval(() => {
				if (window.grecaptcha) {
					clearInterval(checkInterval);
					resolve();
				}
			}, 100);

			// Timeout after 10 seconds
			setTimeout(() => {
				clearInterval(checkInterval);
				if (!window.grecaptcha) {
					reject(new Error("reCAPTCHA script failed to load"));
				} else {
					resolve();
				}
			}, 10000);
			return;
		}

		// Create and load script with site key in render parameter (as per guide)
		const script = document.createElement("script");
		script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
		script.async = true;
		script.defer = true;

		script.onload = () => {
			// Wait for grecaptcha to be available
			const checkInterval = setInterval(() => {
				if (window.grecaptcha) {
					clearInterval(checkInterval);
					resolve();
				}
			}, 100);

			// Timeout after 10 seconds
			setTimeout(() => {
				clearInterval(checkInterval);
				if (!window.grecaptcha) {
					reject(new Error("reCAPTCHA script failed to initialize"));
				} else {
					resolve();
				}
			}, 10000);
		};

		script.onerror = () => {
			reject(new Error("Failed to load reCAPTCHA script"));
		};

		document.head.appendChild(script);
	});
};

/**
 * Custom hook for reCAPTCHA v3 integration
 */
export const useRecaptcha = () => {
	// Get site key from environment variable
	const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY || "";
	const enabled = process.env.REACT_APP_RECAPTCHA_ENABLED === "true";
	const [scriptLoaded, setScriptLoaded] = useState(false);

	// Load script when enabled and site key is available
	useEffect(() => {
		if (enabled && siteKey && !scriptLoaded) {
			loadRecaptchaScript(siteKey)
				.then(() => {
					setScriptLoaded(true);
					console.log("reCAPTCHA script loaded successfully");
				})
				.catch((error) => {
					console.error("Failed to load reCAPTCHA script:", error);
				});
		}
	}, [enabled, siteKey, scriptLoaded]);

	/**
	 * Execute reCAPTCHA and get token
	 * @param action - Action name (e.g., 'login', 'register')
	 * @returns Promise with token or null
	 */
	const executeRecaptcha = useCallback(
		async (action: string): Promise<string | null> => {
			// If reCAPTCHA is disabled, return null
			if (!enabled) {
				console.log("reCAPTCHA is disabled");
				return null;
			}

			// Check if site key is configured
			if (!siteKey) {
				console.error("reCAPTCHA site key not configured");
				return null;
			}

			// Ensure script is loaded
			if (!scriptLoaded && !window.grecaptcha) {
				try {
					await loadRecaptchaScript(siteKey);
					setScriptLoaded(true);
				} catch (error) {
					console.error("Failed to load reCAPTCHA script:", error);
					return null;
				}
			}

			// Check if grecaptcha is loaded
			if (!window.grecaptcha) {
				console.warn("reCAPTCHA not loaded yet");
				return null;
			}

			try {
				// Wait for reCAPTCHA to be ready
				return await new Promise<string>((resolve, reject) => {
					window.grecaptcha.ready(async () => {
						try {
							const token = await window.grecaptcha.execute(siteKey, {
								action: action
							});
							console.log(`reCAPTCHA token generated for action: ${action}`);
							resolve(token);
						} catch (error) {
							console.error("reCAPTCHA execution error:", error);
							reject(error);
						}
					});
				});
			} catch (error) {
				console.error("reCAPTCHA error:", error);
				return null;
			}
		},
		[siteKey, enabled, scriptLoaded]
	);

	return {
		executeRecaptcha,
		isEnabled: enabled,
		siteKey
	};
};

